import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrganisationBranding } from '../../../hooks/useOrganisationBranding';
import { useOnboarding } from '../store/onboarding-store';

// Both `<input type="color">` and the preview theme override need a full 6-digit hex,
// so anything else the API returns is ignored rather than presented as a choice.
export function normaliseHexColour(value: string | null | undefined): string | null {
  if (!value) return null;
  const hex = value.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(hex)) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  return /^[0-9a-f]{6}$/.test(hex) ? `#${hex}` : null;
}

export interface UseBrandingDefaultsResult {
  isLoading: boolean;
  // The organisation's branding, ready to use. Callers apply them as `chosen || preset` so the
  // preset is visible on the render the request settles, without waiting for the store write
  // below — a step that waited a render would mount its picker empty and keep it that way.
  presetColour: string | null;
  presetLogoUrl: string | null;
}

// Presets the colour and logo steps from the organisation's existing app branding so the
// journey can be completed with a single click. Only untouched values are filled —
// whatever the user has already picked or uploaded always wins.
export function useBrandingDefaults(): UseBrandingDefaultsResult {
  const [searchParams] = useSearchParams();
  const {
    organisationId: storedOrganisationId,
    primaryColor,
    logoUrl,
    setPrimaryColor,
    setLogoUrl,
  } = useOnboarding();
  // The outlet mirrors the URL's organisationId into the store, so a step rendered without the
  // query param still knows which organisation the journey belongs to.
  const organisationId = searchParams.get('organisationId') || storedOrganisationId;
  const { branding, isLoading } = useOrganisationBranding(organisationId);

  const presetColour = normaliseHexColour(branding?.brandColour);
  const presetLogoUrl = branding?.logoUrl ?? null;

  // Mirror the presets into the store so later steps and website creation see them even
  // though those screens never fetch branding themselves.
  useEffect(() => {
    if (presetColour && !primaryColor) {
      setPrimaryColor(presetColour);
    }
    if (presetLogoUrl && !logoUrl) {
      setLogoUrl(presetLogoUrl);
    }
  }, [presetColour, presetLogoUrl, primaryColor, logoUrl, setPrimaryColor, setLogoUrl]);

  return { isLoading, presetColour, presetLogoUrl };
}
